// Un mes entero en cuadrícula (lunes a domingo), para el Booking Link.
//
// Lo usan dos pantallas (2026-10-06, pedido de una agencia: bloquear DÍAS, no
// solo horas, y "el calendario más extendido"):
//   · Mi Booking Link → se tocan los días para bloquearlos o desbloquearlos.
//   · La página pública /book/<slug> → el visitante ve el mes completo, con los
//     días que tienen horarios resaltados, en vez de una tira de botones.
//
// No sabe nada de citas: cada pantalla dice, por fecha, cómo se pinta el día y
// qué pasa al tocarlo (`dia(fecha)`). Las fechas son texto "YYYY-MM-DD" en la
// zona del agente — nunca se pasan por `Date` en UTC, que corre el día.

import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DOS = (n) => String(n).padStart(2, "0");
export const fechaDe = (y, m, d) => `${y}-${DOS(m)}-${DOS(d)}`;

/** "2026-10" → el mes siguiente o anterior. */
export function moverMes(mes, paso) {
  const [y, m] = mes.split("-").map(Number);
  const t = y * 12 + (m - 1) + paso;
  return `${Math.floor(t / 12)}-${DOS((t % 12) + 1)}`;
}

/** Las semanas del mes: filas de 7 (lunes primero), con null fuera del mes. */
export function semanasDelMes(mes) {
  const [y, m] = mes.split("-").map(Number);
  const primero = new Date(Date.UTC(y, m - 1, 1)).getUTCDay(); // 0 = domingo
  const hueco = (primero + 6) % 7; // lunes = 0
  const total = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const celdas = [...Array(hueco).fill(null), ...Array.from({ length: total }, (_, i) => fechaDe(y, m, i + 1))];
  while (celdas.length % 7) celdas.push(null);
  const filas = [];
  for (let i = 0; i < celdas.length; i += 7) filas.push(celdas.slice(i, i + 7));
  return filas;
}

const CABECERA = {
  es: ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"],
  en: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
};

/**
 * @param mes        "YYYY-MM"
 * @param onMes      (nuevoMes) => void; sin él no hay flechas
 * @param puedeAntes / puedeDespues  si las flechas están activas
 * @param dia        (fecha) => { fondo, borde, color, tachado, tocable, onClick, titulo }
 * @param colores    { texto, suave, borde, fondo } del tema de la pantalla
 */
export function CalendarioMensual({ mes, onMes, puedeAntes = true, puedeDespues = true, dia, lang = "es", colores = {} }) {
  const c = { texto: "#1a2433", suave: "#94a3b8", borde: "#cbd5e1", fondo: "transparent", ...colores };
  const [y, m] = mes.split("-").map(Number);
  let titulo = mes;
  try {
    titulo = new Intl.DateTimeFormat(lang === "es" ? "es-US" : "en-US", { month: "long", year: "numeric", timeZone: "UTC" })
      .format(new Date(Date.UTC(y, m - 1, 15)));
    titulo = titulo.charAt(0).toUpperCase() + titulo.slice(1); // "Octubre de 2026", no "Octubre De 2026"
  } catch { /* se queda "YYYY-MM" */ }
  const flecha = (activa) => ({
    width: 34, height: 34, borderRadius: 9, border: `1px solid ${c.borde}`, background: c.fondo,
    color: activa ? c.texto : c.suave, opacity: activa ? 1 : .35, cursor: activa ? "pointer" : "default",
    display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0,
  });

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        {onMes ? <button type="button" aria-label={lang === "es" ? "Mes anterior" : "Previous month"} disabled={!puedeAntes} onClick={() => onMes(moverMes(mes, -1))} style={flecha(puedeAntes)}><ChevronLeft size={16} /></button> : <span />}
        <div style={{ fontWeight: 800, fontSize: 15, color: c.texto }}>{titulo}</div>
        {onMes ? <button type="button" aria-label={lang === "es" ? "Mes siguiente" : "Next month"} disabled={!puedeDespues} onClick={() => onMes(moverMes(mes, 1))} style={flecha(puedeDespues)}><ChevronRight size={16} /></button> : <span />}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,minmax(0,1fr))", gap: 6 }}>
        {(CABECERA[lang] || CABECERA.en).map((d) => (
          <div key={d} style={{ textAlign: "center", fontSize: 11.5, fontWeight: 700, color: c.suave, textTransform: "uppercase", letterSpacing: ".04em", paddingBottom: 2 }}>{d}</div>
        ))}
        {semanasDelMes(mes).flat().map((f, i) => {
          if (!f) return <div key={`v${i}`} />;
          const e = dia(f) || {};
          const num = Number(f.slice(8));
          return (
            <button key={f} type="button" disabled={!e.tocable} onClick={e.tocable ? e.onClick : undefined} title={e.titulo || undefined}
              aria-pressed={e.pulsado}
              style={{
                aspectRatio: "1 / 1", minHeight: 38, borderRadius: 10, padding: 0,
                border: e.borde || "1px solid transparent", background: e.fondo || "transparent",
                color: e.color || c.suave, fontWeight: e.tocable ? 800 : 500, fontSize: 14,
                textDecoration: e.tachado ? "line-through" : "none",
                cursor: e.tocable ? "pointer" : "default", fontFamily: "inherit",
              }}>
              {num}
            </button>
          );
        })}
      </div>
    </div>
  );
}
